import React, { useMemo, useState } from 'react'
import './role-capabilities.css'
import { UserRoleEnum } from '../../../models/enums/UserRoleEnum'
import { Permission } from '../../../models/enums/Permission'

const permissionDescriptions: Record<Permission, string> = {
  [Permission.ADMIN]: 'Acesso total ao sistema',
  [Permission.CORE_CHANGE]: 'Pode alterar cadastros e dados centrais',
  [Permission.READ_ONLY]: 'Acesso somente leitura',
  [Permission.AUX_SECRETARY]: 'Apoio em rotinas da secretaria',
  [Permission.ROUTINE_RUN]: 'Executa rotinas e processos administrativos',
  [Permission.STUDENT]: 'Acesso de estudante',
  [Permission.TEACHER]: 'Acesso de professor',
  [Permission.YOUTH_CHANGE]: 'Pode editar recursos de Jovens',
  [Permission.YOUTH_ADMIN]: 'Administração completa do contexto Jovens',
  [Permission.AUX_YOUTH]: 'Apoio operacional no contexto Jovens',
  [Permission.CAMPAIGN_CHANGE]: 'Pode alterar recursos de campanhas',
  [Permission.CAMPAIGN_ADD]: 'Pode criar/adicionar em campanhas',
  [Permission.FINANCE_CHANGE]: 'Pode alterar recursos financeiros'

}

const rolePermissions: Record<UserRoleEnum, Permission[]> = {
  [UserRoleEnum.ALWAYS_ALLOWED]: [],
  [UserRoleEnum.ADMIN]: [Permission.ADMIN, Permission.CORE_CHANGE, Permission.ROUTINE_RUN, Permission.FINANCE_CHANGE, Permission.CAMPAIGN_CHANGE],
  [UserRoleEnum.SECRETARY]: [Permission.CORE_CHANGE],
  [UserRoleEnum.READ]: [Permission.READ_ONLY],
  [UserRoleEnum.AUX_SECRETARY]: [Permission.AUX_SECRETARY],
  [UserRoleEnum.ROUTINE]: [Permission.ROUTINE_RUN],
  [UserRoleEnum.YOUTH]: [Permission.YOUTH_CHANGE, Permission.YOUTH_ADMIN],
  [UserRoleEnum.AUX_YOUTH]: [Permission.YOUTH_CHANGE, Permission.AUX_YOUTH],
  [UserRoleEnum.AUX_CAMPAIGN]: [Permission.CAMPAIGN_ADD],
  [UserRoleEnum.TEACHER]: [Permission.TEACHER],
  [UserRoleEnum.STUDENT]: [Permission.STUDENT],
  [UserRoleEnum.FINANCE]: [Permission.CAMPAIGN_CHANGE, Permission.FINANCE_CHANGE]
}

const roleColors: Record<UserRoleEnum, string> = {
  [UserRoleEnum.ALWAYS_ALLOWED]: '#64748b',
  [UserRoleEnum.ADMIN]: '#1d4ed8',
  [UserRoleEnum.SECRETARY]: '#0f766e',
  [UserRoleEnum.READ]: '#475569',
  [UserRoleEnum.AUX_SECRETARY]: '#2563eb',
  [UserRoleEnum.ROUTINE]: '#7c3aed',
  [UserRoleEnum.YOUTH]: '#be185d',
  [UserRoleEnum.AUX_YOUTH]: '#db2777',
  [UserRoleEnum.AUX_CAMPAIGN]: '#ca8a04',
  [UserRoleEnum.TEACHER]: '#0f766e',
  [UserRoleEnum.FINANCE]: '#15803d',
  [UserRoleEnum.STUDENT]: '#f97316'
}

function getRolePermissions(role: UserRoleEnum): Permission[] {
  return rolePermissions[role] || []
}

export function RoleCapabilitiesList() {
  const [selectedRole, setSelectedRole] = useState<UserRoleEnum>(UserRoleEnum.ALWAYS_ALLOWED)
  const [searchText, setSearchText] = useState('')

  const roles = useMemo(
    () => [
      UserRoleEnum.ADMIN,
      UserRoleEnum.SECRETARY,
      UserRoleEnum.READ,
      UserRoleEnum.AUX_SECRETARY,
      UserRoleEnum.ROUTINE,
      UserRoleEnum.YOUTH,
      UserRoleEnum.AUX_YOUTH,
      UserRoleEnum.AUX_CAMPAIGN,
      UserRoleEnum.TEACHER,
      UserRoleEnum.FINANCE
    ] as UserRoleEnum[],
    []
  )

  const visibleRoles = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase()

    return roles.filter(role => {
      const matchesRoleFilter = selectedRole === UserRoleEnum.ALWAYS_ALLOWED || role === selectedRole
      if (!matchesRoleFilter) {
        return false
      }

      if (!normalizedSearch) {
        return true
      }

      const byRoleName = role.toLowerCase().includes(normalizedSearch)
      const byPermission = getRolePermissions(role).some(permission =>
        permission.toLowerCase().includes(normalizedSearch) ||
        permissionDescriptions[permission].toLowerCase().includes(normalizedSearch)
      )

      return byRoleName || byPermission
    })
  }, [roles, selectedRole, searchText])

  const rolePermissionMap = useMemo(() => {
    return visibleRoles.reduce<Record<UserRoleEnum, Permission[]>>((accumulator, role) => {
      accumulator[role] = getRolePermissions(role)
      return accumulator
    }, {} as Record<UserRoleEnum, Permission[]>)
  }, [visibleRoles])

  const visiblePermissionCount = useMemo(() => {
    const seen = new Set<Permission>()

    visibleRoles.forEach(role => {
      rolePermissionMap[role].forEach(permission => seen.add(permission))
    })

    return seen.size
  }, [rolePermissionMap, visibleRoles])

  const visiblePermissions = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase()

    // Get permissions that are used by visible roles
    const permissionsInVisibleRoles = new Set<Permission>()
    visibleRoles.forEach(role => {
      getRolePermissions(role).forEach(permission => {
        permissionsInVisibleRoles.add(permission)
      })
    })

    // Filter by search if needed
    if (!normalizedSearch) {
      return Array.from(permissionsInVisibleRoles)
    }

    return Array.from(permissionsInVisibleRoles).filter(permission =>
      permission.toLowerCase().includes(normalizedSearch) ||
      permissionDescriptions[permission].toLowerCase().includes(normalizedSearch)
    )
  }, [visibleRoles, searchText])

  return (
    <div id="page-role-capabilities" className="uk-section uk-padding-small">
      <div className="uk-container uk-container-large">
        <div className="uk-card uk-card-default">
          <div className="uk-card-header">
            <h3 className="uk-margin-remove-bottom uk-text-bold">Permissões por Perfil</h3>
            <p className="uk-text-meta uk-margin-small-top">
              Visão das permissões que cada perfil possui no sistema.
            </p>
          </div>

          <div className="uk-card-body">
            <section className="uk-grid-small uk-margin-bottom" uk-grid="true">
              <label className="uk-width-1-2@m uk-form-label">
                <span>Filtrar por perfil</span>
                <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                  <select
                    className="uk-form-small"
                    value={selectedRole}
                    onChange={event => setSelectedRole(event.target.value as UserRoleEnum)}
                  >
                    <option value={UserRoleEnum.ALWAYS_ALLOWED}>Todos</option>
                    {roles.map(role => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  <button
                    className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                    style={{ textTransform: 'none' }}
                    type="button"
                    tabIndex={-1}
                  >
                    <span></span>
                    <span uk-icon="icon: chevron-down"></span>
                  </button>
                </div>
              </label>

              <label className="uk-width-1-2@m uk-form-label">
                <span>Buscar por permissão</span>
                <input
                  value={searchText}
                  onChange={event => setSearchText(event.target.value)}
                  placeholder="Ex.: admin, leitura"
                  className="uk-input uk-form-small"
                />
              </label>
            </section>

            <div className="uk-grid-small uk-child-width-1-3@m uk-margin-bottom" uk-grid="true">
              <div>
                <div className="uk-card uk-card-small uk-card-default">
                  <div className="uk-card-body">
                    <div className="uk-text-small uk-text-muted">Perfis visíveis</div>
                    <div className="uk-text-bold uk-text-lead">{visibleRoles.length}</div>
                  </div>
                </div>
              </div>              
              <div>
                <div className="uk-card uk-card-small uk-card-default">
                  <div className="uk-card-body">
                    <div className="uk-text-small uk-text-muted">Total de permissões</div>
                    <div className="uk-text-bold uk-text-lead">{visiblePermissions.length}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="uk-grid-small" uk-grid="true" uk-masonry="true">
              {visibleRoles.map(role => (
                <div key={`card-${role}`} className="uk-width-1-2@s uk-width-1-3@m">
                  <div className="uk-card uk-card-default">
                    <div className="uk-card-body">
                      <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-bottom">
                        <div>
                          <h3 className="uk-margin-remove">{role}</h3>
                          <div className="uk-text-small uk-text-muted">
                            {rolePermissionMap[role].length} permissão{rolePermissionMap[role].length === 1 ? '' : 's'}
                          </div>
                        </div>
                        <span
                          className="capability-pill"
                          style={{ backgroundColor: roleColors[role], color: '#fff' }}
                        >
                          {role}
                        </span>
                      </div>

                      <div className="uk-flex uk-flex-wrap uk-gap-xsmall">
                        {rolePermissionMap[role].map(permission => (
                          <span
                            key={`${role}-${permission}`}
                            className="capability-pill"
                            title={permissionDescriptions[permission]}
                          >
                            {permission}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <hr className="uk-margin-large" />

            <div>
              <h2 className="uk-margin-bottom">Mapa completo de permissões</h2>
              <div className="uk-grid-small" uk-grid="true" uk-masonry="true">
                {visiblePermissions.map(permission => {
                  const rolesWithPermission = Object.entries(rolePermissions)
                    .filter(([, permissions]) => permissions.includes(permission))
                    .map(([role]) => role as UserRoleEnum)

                  return (
                    <div key={permission} className="uk-width-1-2@s uk-width-1-3@m">
                      <div className="uk-card uk-card-default">
                        <div className="uk-card-body">
                          <h3 className="uk-margin-small">{permission}</h3>
                          <p className="uk-text-small uk-text-muted uk-margin-small">{permissionDescriptions[permission]}</p>
                          <div className="uk-margin-small">
                            <div className="uk-text-xsmall uk-text-muted uk-text-uppercase uk-text-bold">Perfis com permissão</div>
                            <div className="uk-flex uk-flex-wrap uk-gap-xsmall uk-margin-small-top">
                              {rolesWithPermission.map(role => (
                                <span key={`${permission}-${role}`} className="capability-pill">
                                  {role}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoleCapabilitiesList